import { petcoPewterBlue, petcoWhite } from '@common/_assets/color-constants.js';
import { ColorPickerPopover } from '@common/components/forms';
import { YesNoConfirmModal } from '@common/components/modals';
import { ScheduleBlockTypeModel, VeterinarianModel, schedule } from '@common/models';
import { EntityScheduleType, WeekDay } from '@common/models/Enums';
import { getCalendarEventItemId } from '@common/utils';
import FullCalendar, { EventContentArg, EventDropArg, EventInput } from '@fullcalendar/react';
import interactionPlugin, { Draggable, DropArg, EventReceiveArg, EventResizeDoneArg } from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import timeGridPlugin from "@fullcalendar/timegrid";
import { PetcoCalendarTheme } from '@hospital/components/calendar/PetcoCalendarTheme';
import { find, some } from 'lodash';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { v4 as uuid } from 'uuid';

// This component was inspired by https://codesandbox.io/s/vm45zwmo07

type UserSchedulePreferenceEvent = {
    preference: schedule.EntitySchedulePreference | undefined;
} & EventInput

// our schedule preferences do not care about dates, but Fullcalendar very much does.
const eventDate = "2000-01-01"

const defaultProfileColor = petcoPewterBlue;
/**
 ** Event creation and manipulation
 **/

// Creates a colored schedule block element that will be draggable when dragging/dropping is enabled.
const draggableScheduleBlockType = (sb: ScheduleBlockTypeModel, editing: boolean) =>
    <span key={sb.scheduleBlockTypeId}
        id={sb.scheduleBlockTypeId.toString()}
        role="listitem"
        title={sb.name}
        className='schedule-block'
        style={{
            color: sb.color,
            backgroundColor: `${sb.color}25`,
            cursor: editing ? "pointer" : "auto",
            border: editing ? `1px solid ${sb.color}` : `1px solid ${sb.color}05`
        }}>
        {sb.name}
    </span>

// Make schedule block elements draggable into the grid.
const initDraggableBlocks = () =>
    new Draggable(document.getElementById("schedule-block-list")!, {
        itemSelector: ".schedule-block",
        eventData: (eventEl: HTMLElement) => ({
            id: eventEl.getAttribute("data"),
            title: eventEl.getAttribute("title")
        })
    });


/**
 ** Event creation and manipulation
 **/

// generate a new event record with a title and appropriate coloring
const newEvent = (pref: schedule.EntitySchedulePreference): UserSchedulePreferenceEvent => {
    var baseEntityPreferenceId = getCalendarEventItemId(pref.uniqueId);
    return {
        id: baseEntityPreferenceId === 0 ? `0_${uuid()}` : pref.uniqueId,
        title: pref.scheduleBlockTypeName,
        textColor: pref.scheduleBlockTypeColor,
        backgroundColor: `${pref.scheduleBlockTypeColor}25`,
        borderColor: petcoWhite,
        preference: pref
    };
}

// modify the start/end time of the event
const updateEventStartEnd = (event: UserSchedulePreferenceEvent, start: string, end?: string) => {
    event.start = start;
    event.end = end ? end : DateTime.fromISO(start).plus({ hours: 1 }).toISO();
    return event;
}

// modify the resource (day) of the event.
const updateEventResource = (event: UserSchedulePreferenceEvent, resourceId?: string) => {
    event.resourceId = resourceId ?? event.resourceId;
    event.preference!.dayOfWeek = parseInt(event.resourceId!);
    return event;
}

/**
 * Event <-> Preference mapping
 */

const preferenceToEvent = (p: schedule.EntitySchedulePreference): UserSchedulePreferenceEvent => {
    const event = newEvent(p);
    // convert time to date: 08:00:00 -> 2000-01-01T08:00:00
    updateEventStartEnd(event, `${eventDate}T${p.startTime}`, `${eventDate}T${p.endTime}`);
    updateEventResource(event, p.dayOfWeek.valueOf().toString());
    return event;
};

const eventToPreference = (e: UserSchedulePreferenceEvent): schedule.EntitySchedulePreference => {
    const pref = e.preference!;

    return new schedule.EntitySchedulePreference({
        entityPreferenceId: pref.entityPreferenceId,
        entityId: pref.entityId,
        entityType: EntityScheduleType.Veterinarian,
        schedulePreferenceId: pref.schedulePreferenceId,
        scheduleBlockTypeId: pref.scheduleBlockTypeId,
        scheduleBlockTypeName: pref.scheduleBlockTypeName,
        scheduleBlockIsOverrideType: false,
        scheduleBlockTypeColor: pref.scheduleBlockTypeColor,
        rowVersion: pref.rowVersion,
        uniqueId: '',
        hospitalId: 0,
        startTime: DateTime.fromISO((e.start as string)).toISOTime({ includeOffset: false, suppressMilliseconds: true }),
        endTime: DateTime.fromISO((e.end as string)).toISOTime({ includeOffset: false, suppressMilliseconds: true }),
        dayOfWeek: parseInt(e.resourceId!)
    });
}

/**
 * Component
 */
interface IProps {
    readOnly?: boolean;
    scheduleBlockTypes: ScheduleBlockTypeModel[];
    veterinarian: VeterinarianModel;
    onSavePreferences: (preferences: schedule.EntitySchedulePreference[], schedulingColor: string) => void;
    isActive: boolean;
}

export const UserSchedulePreferences = observer((props: IProps) => {
    const [editing, setEditing] = useState(false);
    const [schedulingColor, setSchedulingColor] = useState(props.veterinarian.schedulingColor ? props.veterinarian.schedulingColor : defaultProfileColor);
    const [events, setEvents] = useState<UserSchedulePreferenceEvent[]>([]);
    const [promptSaveChanges, setPromptSaveChanges] = useState(false);

    // initialize the calendar grid events from the current set of vet schedule preferences
    useEffect(() => {
        if (props.veterinarian?.schedulePreferences) {
            setEvents(props.veterinarian.schedulePreferences.map(preferenceToEvent))
        }
    }, [props.veterinarian]);

    /**
     * Dragging/dropping is enabled when a Draggable instance is created
     * To disable dragging/dropping we must call Draggable.destroy().
     * This reference tracks a Draggable instance and enable/disable dragging as needed.
     */
    const draggable = useRef<Draggable | undefined>(undefined);

    //
    const hasExistingPreferences = some(props.veterinarian?.schedulePreferences);

    // generate the markup for the grid event
    const renderEvent = (arg: EventContentArg) => {
        const event = find(events, { id: arg.event.id });
        return (
            <div data-testid={`event-${arg.event.id}`}>
                <div className="d-flex flex-column">
                    <div className="schedule-block__event-title fw-bold">{arg.event.title}</div>
                    <div className="schedule-block__event-time">{arg.timeText}</div>
                </div>
                {editing && event?.resourceId &&
                    <div role="button" className="schedule-block__delete"
                        aria-label={`Delete ${WeekDay.getLongName(parseInt(event.resourceId))} ${arg.timeText} ${arg.event.title}`}
                        onClick={() => deleteEvent(arg.event.id)}>
                        <i className="fa fa-trash-o" aria-hidden="true"></i>
                    </div>
                }
            </div>
        )
    }


    // A schedule block was dragged into the grid. Create a corresponding event record.
    const createEvent = (arg: DropArg) => {
        const schedBlock = find(props.scheduleBlockTypes, { name: arg.draggedEl.title })!;
        const newPref = schedule.EntitySchedulePreference.createNewEntitySchedulePreference(
            {
                scheduleBlockTypeId: schedBlock.scheduleBlockTypeId,
                scheduleBlockTypeColor: schedBlock.color,
                scheduleBlockTypeName: schedBlock.name,
                scheduleBlockIsOverrideType: false
            });
        const event = newEvent(newPref);
        updateEventStartEnd(event, arg.dateStr)
        updateEventResource(event, arg.resource?.id);
        setEvents([...events, event]);
    };

    /**
     * When you drag a schedule block onto the grid, FullCalendar adds a stub event to the grid and
     * then fires 'eventReceive'. It fires 'drop' at the same time. Only 'drop' carries the necessary
     * metadata to properly create an event descriptor. (In particular, the 'eventReceive' metadata
     * is missing the event day, or "resource".)
     * Since the 'drop' args are sufficient to create the event, we don't need FullCalendar's stub event.
     * This handler simply tells FullCalendar to revert the addition of that stub event to the grid.
     */
    const receiveEvent = (arg: EventReceiveArg) => { arg.revert() };

    // The event end time was changed.
    const resizeEvent = (arg: EventResizeDoneArg) => {
        const event = find(events, { id: arg.event.id })!;
        updateEventStartEnd(event, arg.event.startStr, arg.event.endStr);
        setEvents(events);
    }

    // The event was dragged to a different time time slot and/or day
    const moveEvent = (arg: EventDropArg) => {
        const event = find(events, { id: arg.event.id })!;
        updateEventStartEnd(event, arg.event.startStr, arg.event.endStr);
        updateEventResource(event, arg.newResource?.id);
        setEvents(events);
    }

    // Remove the selected event from state
    const deleteEvent = (eventId: string) => {
        setEvents(events.filter(e => e.id != eventId));
    }

    // Enable schedule block dragging/dropping
    const enableEditing = () => {
        draggable.current?.destroy();
        draggable.current = initDraggableBlocks();
        setEditing(true);
    }

    // Disable schedule block dragging/dropping
    const disableEditing = () => {
        draggable.current?.destroy();
        setEditing(false);
        setPromptSaveChanges(false)
    }

    // Disable grid interaction and restore gird to original state
    const cancelChanges = () => {
        disableEditing();
        setEvents(props.veterinarian.schedulePreferences.map(preferenceToEvent));
        setSchedulingColor(props.veterinarian.schedulingColor ? props.veterinarian.schedulingColor : defaultProfileColor);
    }

    // Disable grid interaction and post events back to server
    const saveChanges = () => {
        disableEditing();
        props.onSavePreferences(events.map(e => eventToPreference(e)), schedulingColor);
    }

    // Prompt the use to confirm changes to preferences.
    // Don't prompt them if they're adding preferences for the first time.
    const confirmSaveChanges = () => {
        if (hasExistingPreferences)
            setPromptSaveChanges(true);
        else
            saveChanges();
    }

    return (<>
        <YesNoConfirmModal
            visible={promptSaveChanges}
            busy={false}
            title='Update schedule preferences'
            description='Changes to your schedule preferences are immediately reflected in hospital scheduling upon saving and will not impact appointments already booked.'
            confirmButtonText='Save Changes'
            onConfirm={saveChanges}
            onCancel={cancelChanges}
        />
        <Row>
            {!props.readOnly && props.isActive &&
                <div className="d-flex flex-row-reverse gap-2">
                    {!editing && !hasExistingPreferences && <Button variant="outline-primary" onClick={enableEditing}>Add Preferences</Button>}
                    {!editing && hasExistingPreferences && <Button variant="outline-primary" onClick={enableEditing}>Edit Preferences</Button>}
                    {editing && <Button variant="primary" onClick={confirmSaveChanges}>Save Preferences</Button>}
                    {editing && <Button variant="outline-primary" onClick={cancelChanges}>Cancel</Button>}
                </div>
            }


            <Form.Label>Profile Color</Form.Label>
            <InputGroup>
                <div className="d-flex flex-fill flex-row">
                    <div>
                        {editing &&
                            <ColorPickerPopover color={schedulingColor} onChange={(newColor) => {
                                setSchedulingColor(newColor);
                            }} />
                        }
                    </div>
                    <div>
                        <span className='schedule-block'
                            style={{
                                color: petcoWhite,
                                backgroundColor: schedulingColor ? schedulingColor : defaultProfileColor,
                                cursor: "auto",
                                border: `1px solid 00195205`
                            }}>
                            {`Dr. ${props.veterinarian.firstName} ${props.veterinarian.lastName}`}
                        </span>
                    </div>
                </div>
            </InputGroup>

        </Row>
        <Row className="mt-3">
            <Col md={12}>
                <div id="schedule-block-list" className="schedule-block-list d-flex flex-row mt-2 p-1">
                    <div>Available Blocks:</div>
                    <div role="list">
                        {props.scheduleBlockTypes.map(sb => draggableScheduleBlockType(sb, editing))}
                    </div>
                </div>
            </Col>
        </Row>
        <Row>
            <FullCalendar
                themeSystem='petco'
                timeZone='utc'
                initialDate={eventDate}
                initialView='resourceTimeGridDay'
                plugins={[PetcoCalendarTheme.createPlugin(), resourceTimeGridPlugin, interactionPlugin, timeGridPlugin]}
                resources={WeekDay.FullResources}
                events={events}
                eventContent={renderEvent}
                eventOverlap={false}
                eventResizableFromStart={true}
                editable={editing}
                droppable={editing}
                allDaySlot={false}
                height='auto'
                headerToolbar={{ start: '', center: '', right: '' }}
                slotMinTime="07:00:00"
                slotMaxTime="19:00:00"
                slotDuration="00:30:00"
                slotLabelInterval="00:30:00"
                slotLabelFormat={{ hour: 'numeric', minute: '2-digit', }}
                slotLabelClassNames={"fc-dvm-schedule-preference-slot"}
                slotLaneClassNames={"fc-dvm-schedule-preference-slot"}
                eventClassNames={"fc-dvm-schedule-preference-event"}
                eventDrop={moveEvent}
                eventResize={resizeEvent}
                eventReceive={receiveEvent}
                drop={createEvent}
            />
        </Row>
        <Row className="mt-3">
            <div className="d-flex gap-2 note">
                <div><i className="fa fa-circle schedule-block__appointments" aria-hidden="true"></i></div>
                <div>Online booking only available for appointments.</div>
            </div>
        </Row>
    </>)
});
